import React, {useCallback, useEffect, useRef, useState} from "react";
import {ControlButton, Controls, Edge, Node, ReactFlow, useReactFlow} from "@xyflow/react";
import {NO_OP} from "../../../constants/common";
import '@xyflow/react/dist/style.css';
import FirstLevelNode from "./Nodes/FirstLevelNode";
import {EstateFlowchartState, FlowchartEstateType} from "../../model/EstateFlowchartTypes";
import {useAppSelector} from "../../../store/hooks";
import {familyMembersSelector} from "../../slices/EstateFlowChartSlice";
import {
    applyDagreLayout,
    centerSelectedCard,
    DISTRIBUTION_DIMENSIONS,
    getEstateTypeNodes,
} from "../../utils/FlowchartVisualUtils";
import FlowchartCustomEdge from "./Edges/FlowchartCustomEdge";
import DistributionsNode from "./Nodes/DistributionsNode";
import {Icon} from "../../../components";
import {EstateCardViewModeType} from "../EstateCard/EstateCard";
import {hasWillSummary} from "../../utils/DefUtils";
import EstateTypeParentNode from "./Nodes/EstateTypeParentNode";
import FirstLevelParentNode from "./Nodes/FirstLevelParentNode";
import {selectReleaseToggles} from "../../../ReleaseToggles/releaseTogglesSlice";

type FlowchartVisualMainProps = {
    estateType: FlowchartEstateType,
    flowcharts: EstateFlowchartState[],
    flowchartId: string
    isInReportSplitView?: boolean,
    width?: number,
    selectedCardId?: string,
    selectedCardRef?: React.RefObject<HTMLDivElement>,
}

const FlowchartVisualMain: React.FC<FlowchartVisualMainProps> = ({
                                                                     estateType,
                                                                     flowcharts,
                                                                     flowchartId,
                                                                     isInReportSplitView,
                                                                     width = 1366,
                                                                     selectedCardId,
                                                                     selectedCardRef
                                                                 }) => {
    const selectedFlowchart = flowcharts.find(fc => fc.flowchartId === flowchartId);
    const familyMembers = useAppSelector(familyMembersSelector);
    const fiduciaries = useAppSelector(state => state.estateFlowchart.fiduciaries);
    const enableEstateFlowchartMVPRelease = useAppSelector(selectReleaseToggles)?.enableEstateFlowchartMVPRelease;
    const enableWildCardInEstateFlowchart = useAppSelector(selectReleaseToggles)?.enableWildCardInEstateFlowchart;
    const enableAdditionalDistributionLayer = useAppSelector(selectReleaseToggles)?.enableAdditionalDistributionLayer;

    const [nodes, setNodes] = useState<Node[]>([]);
    const [edges, setEdges] = useState<Edge[]>([]);
    const [flowchartWidth, setFlowchartWidth] = useState<number>(0);
    const [flowchartHeight, setFlowchartHeight] = useState<number>(0);
    const [initialRenderCompleted, setInitialRenderCompleted] = useState<boolean>(false);

    const flowchartVisualMainRef = useRef<HTMLDivElement>(null);

    const reactFlowInstance = useReactFlow();

    const nodeTypes = {
        firstLevelNode: FirstLevelNode,
        distributionsNode: DistributionsNode,
        firstLevelParentNode: FirstLevelParentNode,
        estateTypeParentNode: EstateTypeParentNode,
    };

    const edgeTypes = {
        customEdge: FlowchartCustomEdge
    }

    const centerAlignSelectedCard = useCallback(() => {
        if (!flowchartVisualMainRef.current
            || !selectedCardRef?.current
            || (selectedCardRef.current.id !== `estate-card-${selectedCardId}`)) {
            setTimeout(centerAlignSelectedCard, 15);
            return;
        }

        const cardDomRect = selectedCardRef.current.getBoundingClientRect();
        if (!cardDomRect) {
            return;
        }

        const localNodes = centerSelectedCard(cardDomRect, width,
            flowchartVisualMainRef.current.getBoundingClientRect(), reactFlowInstance.getNodes()
        )

        setNodes(localNodes)
    }, [isInReportSplitView, selectedCardId, selectedCardRef?.current, flowchartVisualMainRef.current]);

    const fitFlowchartToView = useCallback((originalFlowchartWidth: number, originalFlowchartHeight: number) => {
        const viewportWidth = width ?? 1366;
        const viewportHeight = 800;

        if ((originalFlowchartWidth > viewportWidth || originalFlowchartHeight > viewportHeight) && !isInReportSplitView) {
            reactFlowInstance.fitView().then(() => {
                reactFlowInstance.setViewport({
                    y: 0,
                    x: reactFlowInstance.getViewport().x,
                    zoom: reactFlowInstance.getZoom()
                }).then();
            });
        } else {
            reactFlowInstance.setViewport({
                y: 0,
                x: 0,
                zoom: 1
            }).then();
        }
    }, [reactFlowInstance]);

    // Use useLayoutEffect to ensure DOM is updated before fitting
    React.useLayoutEffect(() => {
        // Only fit if nodes and dimensions are set and flowchart is selected
        if (
            selectedFlowchart &&
            nodes.length > 0 &&
            flowchartWidth > 0 &&
            flowchartHeight > 0 &&
            reactFlowInstance
        ) {
            fitFlowchartToView(flowchartWidth, flowchartHeight);
        }
    }, [selectedFlowchart, nodes, flowchartWidth, flowchartHeight]);

    useEffect(() => {
        const waitForSelectedCard = () => {
            if (selectedCardRef?.current && isInReportSplitView && selectedCardId && initialRenderCompleted) {
                centerAlignSelectedCard();
            } else {
                setTimeout(waitForSelectedCard, 50); // Retry every 50ms
            }
        };

        waitForSelectedCard();
    }, [isInReportSplitView, selectedCardId, selectedCardRef?.current, initialRenderCompleted]);

    useEffect(() => {
        if (isInReportSplitView && initialRenderCompleted) {
            return;
        }

        if (selectedFlowchart) {
            const viewMode = selectedFlowchart.viewMode ?? EstateCardViewModeType.SIMPLE;
            const {
                nodes,
                edges,
                flowchartHeight,
                flowchartWidth
            } = getEstateTypeNodes(
                selectedFlowchart,
                estateType,
                familyMembers,
                hasWillSummary(selectedFlowchart.willSummary).hasWills ? fiduciaries : undefined,
                {
                    enableEstateFlowchartMVPRelease,
                    enableWildCardInEstateFlowchart,
                    enableAdditionalDistributionLayer,
                },
                selectedCardRef,
                selectedCardId
            );

            let updatedNodes: Node[] = [];
            let inEstateParentNode: Node | undefined = nodes.find(node => node.id === "IN_ESTATE-parent-node")
            let outOfEstateParentNode: Node | undefined = nodes.find(node => node.id === "OUT_OF_ESTATE-parent-node");

            // Apply dagre layout to top-level nodes (IN_ESTATE and OUT_OF_ESTATE parent nodes without a parent)
            updatedNodes.push(...applyDagreLayout(nodes.filter(node => node.parentId === undefined), [], "TB", 64, 1366));

            // Apply dagre layout to in-estate and out-of-estate nodes within their respective parent nodes
            if (inEstateParentNode?.width) {
                let inEstateNodes: Node[] = nodes.filter(node => node.parentId === "IN_ESTATE-parent-node")
                updatedNodes.push(...applyDagreLayout(inEstateNodes, edges, "TB", DISTRIBUTION_DIMENSIONS[viewMode].GRID_GAP, inEstateParentNode.width))
            }

            // Similarly for out-of-estate nodes
            if (outOfEstateParentNode?.width) {
                let outOfEstateNodes: Node[] = nodes.filter(node => node.parentId === "OUT_OF_ESTATE-parent-node")
                updatedNodes.push(...applyDagreLayout(outOfEstateNodes, edges, "TB", DISTRIBUTION_DIMENSIONS[viewMode].GRID_GAP, outOfEstateParentNode.width))
            }

            // Apply dagre layout to child nodes within each first-level parent node
            nodes.filter(node => node.type === "firstLevelParentNode")
                .forEach(parentNode => {
                    const childNodes = nodes.filter(node => node.parentId === parentNode.id);
                    if (childNodes.length > 0) {
                        updatedNodes.push(...applyDagreLayout(childNodes, edges, "LR", DISTRIBUTION_DIMENSIONS[viewMode].GRID_GAP, parentNode.width, true));
                    }
                })

            setNodes(updatedNodes);
            setEdges(edges);
            setFlowchartHeight(flowchartHeight);
            setFlowchartWidth(flowchartWidth);

            if (isInReportSplitView && !initialRenderCompleted) {
                setInitialRenderCompleted(true)
            }
        }
    }, [selectedFlowchart, estateType, enableEstateFlowchartMVPRelease, initialRenderCompleted, isInReportSplitView, enableAdditionalDistributionLayer]);

    return (
        <div className={"flowchart-visual-main"}>
            <ReactFlow nodes={nodes}
                       nodeTypes={nodeTypes}
                       edges={edges}
                       edgeTypes={edgeTypes}
                       style={{backgroundColor: '#f0f0f0', height: "100%"}}
                       zoomOnPinch={false}
                       zoomOnScroll={false}
                       zoomOnDoubleClick={false}
                       preventScrolling={false}
                       onNodeClick={NO_OP}
                       nodesDraggable={false}
                       nodesConnectable={false}
                       minZoom={0.4}
                       maxZoom={2}
                       ref={flowchartVisualMainRef}
            >
                <Controls showInteractive={false} showFitView={false} showZoom={false} style={{left: 16, bottom: 16}}>
                    <ControlButton
                        onClick={() => reactFlowInstance.zoomIn()}
                        aria-label="Zoom In"
                    >
                        <Icon name="zoom_in" className={"control-icon"}/>
                    </ControlButton>
                    <ControlButton
                        onClick={() => reactFlowInstance.zoomOut()}
                        aria-label="Zoom Out"
                    >
                        <Icon name="zoom_out" className={"control-icon"}/>
                    </ControlButton>
                    <ControlButton
                        onClick={() => fitFlowchartToView(flowchartWidth, flowchartHeight)}
                        aria-label="Fit View"
                    >
                        <Icon name="fullscreen_exit" className={"control-icon"}/>
                    </ControlButton>
                </Controls>
            </ReactFlow>
        </div>
    );
};

export default FlowchartVisualMain;